import React, { useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  FunnelIcon,
  ClockIcon,
  UserIcon,
  ComputerDesktopIcon
} from '@heroicons/react/24/outline';

const SecurityLogs = () => {
  const [filters, setFilters] = useState({
    level: '',
    action: '',
    userId: '',
    targetUserId: '',
    limit: 50,
    offset: 0
  });

  const { data: logsData, isLoading } = useQuery(
    ['security-logs', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axios.get(`/api/super-admin/security-logs?${params}`);
      return response.data.data;
    },
    {
      refetchInterval: 30000 // Rafraîchir toutes les 30 secondes
    }
  );

  const getLevelIcon = (level) => {
    switch (level) {
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  const getLevelBadge = (level) => {
    const styles = {
      info: 'bg-blue-100 text-blue-800',
      warning: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[level] || styles.info}`}>
        {level.toUpperCase()}
      </span>
    );
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0 // Reset pagination when filters change
    }));
  };

  const loadMore = () => {
    setFilters(prev => ({
      ...prev,
      offset: prev.offset + prev.limit
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des logs de sécurité...</p>
        </div>
      </div>
    );
  }

  const { logs, stats, pagination } = logsData || { logs: [], stats: {}, pagination: {} };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-8 w-8 text-red-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Logs de Sécurité</h1>
              <p className="mt-2 text-gray-600">
                Surveillance des changements de rôles et actions sensibles
              </p>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <InformationCircleIcon className="h-8 w-8 text-blue-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Logs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Avertissements</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.byLevel?.warning || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Erreurs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.byLevel?.error || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <UserIcon className="h-8 w-8 text-purple-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Changements de Rôle</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.byAction?.['Changement de rôle utilisateur'] || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center mb-4">
            <FunnelIcon className="h-5 w-5 text-gray-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Filtres</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Niveau</label>
              <select
                value={filters.level}
                onChange={(e) => handleFilterChange('level', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Tous les niveaux</option>
                <option value="info">Info</option>
                <option value="warning">Avertissement</option>
                <option value="error">Erreur</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <input
                type="text"
                placeholder="Rechercher une action..."
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Utilisateur</label>
              <input
                type="text"
                placeholder="ID utilisateur..."
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Utilisateur Cible</label>
              <input
                type="text"
                placeholder="ID utilisateur cible..."
                value={filters.targetUserId}
                onChange={(e) => handleFilterChange('targetUserId', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Limite</label>
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>
        </div>

        {/* Liste des logs */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Logs de Sécurité ({pagination.total || 0} entrées)
            </h3>
          </div>
          
          <div className="divide-y divide-gray-200">
            {logs.map((log) => (
              <div key={log.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    {getLevelIcon(log.level)}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {getLevelBadge(log.level)}
                        <span className="text-sm font-medium text-gray-900">
                          {log.action}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 mb-2">{log.message}</p>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {formatDate(log.timestamp)}
                        </div>
                        
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 mr-1" />
                          {log.userName}
                        </div>
                        
                        {log.targetUserName && (
                          <div className="flex items-center">
                            <span className="mr-1">→</span>
                            {log.targetUserName}
                          </div>
                        )}
                        
                        <div className="flex items-center">
                          <ComputerDesktopIcon className="h-4 w-4 mr-1" />
                          {log.ipAddress}
                        </div>
                      </div>
                      
                      {log.details && (
                        <div className="mt-3 p-3 bg-gray-100 rounded-md">
                          <details>
                            <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                              Détails techniques
                            </summary>
                            <pre className="mt-2 text-xs text-gray-600 overflow-x-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </details>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {pagination.hasMore && (
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={loadMore}
                className="w-full bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors"
              >
                Charger plus de logs
              </button>
            </div>
          )}
          
          {logs.length === 0 && (
            <div className="p-12 text-center">
              <ShieldCheckIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun log trouvé
              </h3>
              <p className="text-gray-600">
                Aucun log de sécurité ne correspond aux critères sélectionnés.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityLogs;

